// program_match.js — the onboarding program matcher (pure, no React).
//
// Picks what a knowledgeable coach would pick from the template library for a
// profile { goal, experience, daysPerWeek, sessionMinutes, equipment |
// equipmentPreset, injuries }, and says why in plain language.
//
//   matchPrograms(profile)   → [{ template, score, equipOk, reasons[], warnings[], why }]  best first
//   explainMatch(t, profile) → { headline, lines[] }  plain-language "why this program"
//   templateStyle(t)         → { style, complexity }  how a program progresses, 1 (simple) … 3
//   injuryAreas(text)        → ['lowerBack', 'knees', …] from the free-text / chip answer
//   stressLoad(t, areas)     → weekly sets of exercises that load those areas hard
//
// How the ranking thinks, in priority order:
//   1. Equipment is a hard requirement (a program you can't do is never first).
//   2. Experience: beginners get simple programs (double progression or a
//      linear novice plan); periodized blocks, peaking plans, 10×10 and other
//      specialization blocks are never someone's first pick.
//   3. Days and minutes: never more days than they have; a program that runs
//      well past their session length loses to one that fits.
//   4. Goal, then a coach's preference order inside each goal × level (the
//      PREFER table below), then injuries steer away from programs built on
//      the lifts that load the sore area.
// tools/smoke-68-onboarding-match.mjs checks every combination of answers.

import { TEMPLATES, profileEquipment } from './program_templates.js';

const GROUP = { new: 'N', beginner: 'N', intermediate: 'I', advanced: 'A' };

// A coach's first choices per goal × level, best first. Equipment, days and
// time decide which of these actually fits; the order breaks ties.
const PREFER = {
  hypertrophy: {
    N: ['fb3-beginner', 'ul4-hyp', 'ppl6-linear', 'db3', 'db4-ul', 'min2', 'min2-db', 'express3', 'ul4-novice', 'db-ppl3', 'ppl3', 'bw3', 'cali3', 'kb3', 'express20-db', 'express20-bw'],
    I: ['ul4-hyp', 'meso-fb3', 'ulppl5', 'ppl6', 'meso-ul', 'db4-ul', 'db3', 'db-ppl3', 'min2', 'min2-db', 'ppl3', 'bro5', 'meso-ppl', 'rtf4', 'phul', 'express3', 'cali4', 'cali3', 'kb3', 'bw3', 'express20-db', 'express20-bw'],
    A: ['meso-ul', 'ul4-hyp', 'meso-fb3', 'ulppl5', 'meso-ppl', 'ppl6', 'rtf4', 'bro5', 'arnold6', 'db4-ul', 'db3', 'db-ppl3', 'min2', 'min2-db', 'phul', 'express3', 'cali4', 'cali3', 'kb3', 'bw3', 'express20-db', 'express20-bw'],
  },
  strength: {
    N: ['lp-5x5', 'novice-3x5', 'ul4-novice', 'lp-amrap', 'min2', 'fb3-beginner', 'db3', 'db4-ul', 'min2-db', 'express3', 'cali3', 'kb3', 'bw3', 'express20-db', 'express20-bw'],
    I: ['wave-fsl3', 'gzcl-tiered', 'block6', 'pb5-top', 'dup3', 'vri3', 'rpe-fb3', 'wave-bv4', 'phat5', 'phul', 'min2', 'cali4', 'db4-ul', 'db3', 'min2-db', 'kb3', 'cali3', 'express3', 'bw3', 'express20-db', 'express20-bw'],
    A: ['block6', 'dup3', 'pyramid5', 'pyramid4', 'wave-fsl3', 'pb5-top', 'gzcl-tiered', 'rpe-fb3', 'wave-bv4', 'phat5', 'min2', 'cali4', 'db4-ul', 'db3', 'min2-db', 'kb3', 'express3', 'bw3', 'express20-db', 'express20-bw'],
  },
  powerbuilding: {
    N: ['ul4-novice', 'lp-amrap', 'fb3-beginner', 'lp-5x5', 'ppl6-linear', 'min2', 'db4-ul', 'db3', 'min2-db', 'express3', 'cali3', 'kb3', 'bw3', 'express20-db', 'express20-bw'],
    I: ['phul', 'rpe-fb3', 'pb4', 'phat5', 'pb5-top', 'ppl6', 'wave-bv4', 'ulppl5', 'ul4-hyp', 'gzcl-tiered', 'min2', 'db4-ul', 'db3', 'min2-db', 'cali4', 'kb3', 'express3', 'bw3', 'express20-db', 'express20-bw'],
    A: ['pb4', 'rpe-fb3', 'phat5', 'pb5-top', 'wave-bv4', 'phul', 'ppl6', 'ulppl5', 'ul4-hyp', 'min2', 'db4-ul', 'db3', 'min2-db', 'cali4', 'kb3', 'express3', 'bw3', 'express20-db', 'express20-bw'],
  },
  general: {
    N: ['fb3-beginner', 'min2', 'ul4-novice', 'express3', 'min2-db', 'db3', 'db4-ul', 'active2', 'kb3', 'cali3', 'bw3', 'kb2', 'joint3', 'express20-db', 'express20-bw'],
    I: ['meso-fb3', 'ul4-hyp', 'min2', 'wave-fsl3', 'db3', 'db4-ul', 'min2-db', 'joint3', 'kb3', 'cali4', 'cali3', 'athlete3', 'express3', 'bw3', 'kb2', 'express20-db', 'express20-bw'],
    A: ['ul4-hyp', 'meso-fb3', 'wave-fsl3', 'dup3', 'min2', 'athlete3', 'db4-ul', 'db3', 'min2-db', 'cali4', 'kb3', 'express3', 'bw3', 'express20-db', 'express20-bw'],
  },
};

// Programs that are a short block or a specific purpose, not a base program.
const ROLE = {
  gvt3: 'volume-block', peak12: 'peak', peak8: 'peak', bench6: 'specialization',
  glute4: 'specialization', 'glute-meso': 'specialization', glute3: 'specialization',
  strongman3: 'specialization', athlete3: 'specialization', arnold6: 'high-volume',
  joint3: 'joint', active2: 'aging', express3: 'express', 'express20-db': 'express', 'express20-bw': 'express',
};

// ——— How a program progresses ————————————————————————————————————————————

const STYLE_RANK = { linear: 1, double: 1, gzcl: 2, rp_sets: 2, rpe_topset: 3, percent: 3 };
export function templateStyle(t) {
  const schemes = new Set();
  for (const d of t.days || []) for (const e of d.exercises || []) schemes.add(e.scheme || 'double');
  let style = 'double';
  for (const s of ['percent', 'rpe_topset', 'rp_sets', 'gzcl', 'linear']) if (schemes.has(s)) { style = s; break; }
  let complexity = Math.max(...[...schemes].map(s => STYLE_RANK[s] || 1));
  if (ROLE[t.id] === 'peak') complexity = 3;
  return { style, complexity, role: ROLE[t.id] || null };
}

// ——— Injuries ——————————————————————————————————————————————————————————

const AREA_WORDS = [
  ['lowerBack', /\b(lower )?back\b|lumbar|spine|disc|sciatic/],
  ['knees', /\bknee/],
  ['shoulders', /shoulder|rotator|labrum/],
  ['wrists', /wrist|elbow|tennis elbow|golfer/],
  ['hips', /\bhip/],
  ['neck', /\bneck|cervical/],
];
export function injuryAreas(text) {
  const s = String(text || '').toLowerCase();
  return AREA_WORDS.filter(([, re]) => re.test(s)).map(([id]) => id);
}
const areaPhrase = (areas) => 'your ' + joinPhrase(areas.map(a => AREA_LABEL[a]));
const complainS = (areas) => (areas.length > 1 || /s$/.test(AREA_LABEL[areas[0]]) ? '' : 's');
export const AREA_LABEL = { lowerBack: 'lower back', knees: 'knees', shoulders: 'shoulders', wrists: 'wrists and elbows', hips: 'hips', neck: 'neck' };

// Exercises that load an area hard (weight 1) or noticeably (0.5).
const STRESS = {
  lowerBack: [[/^back squat|conventional deadlift|^sumo deadlift|bent over barbell row|good morning|rack pulls|zercher|power clean|clean and press/i, 1], [/romanian deadlift|stiff-legged|front squat|barbell lunge|hyperextension/i, 0.5]],
  knees: [[/^back squat|front squat|bulgarian split|lunge|jump|leg extension|hack squat|single-leg high box|sissy|step.?up|bound/i, 1], [/leg press|goblet squat|zercher/i, 0.5]],
  shoulders: [[/overhead press|push press|military|handstand|dips|upright row|clean and press|arnold/i, 1], [/barbell bench press|incline bench|incline smith|close-grip barbell bench|seated dumbbell press|standing db shoulder|machine shoulder press/i, 0.5]],
  wrists: [[/front squat|barbell curl|ez-bar curl|skull crusher|power clean|clean and press|handstand/i, 1], [/push-?up|pushups|dips|close-grip barbell bench|preacher curl|kettlebell clean/i, 0.5]],
  hips: [[/^back squat|conventional deadlift|sumo deadlift|lunge|bulgarian split|zercher|good morning/i, 1], [/romanian deadlift|hip thrust|front squat|step.?up/i, 0.5]],
  neck: [[/shrug|upright row|^back squat|overhead press|push press/i, 1], [/farmer|deadlift/i, 0.5]],
};
export function stressLoad(t, areas) {
  if (!areas || !areas.length) return 0;
  let n = 0;
  for (const d of t.days || []) for (const e of d.exercises || []) {
    for (const a of areas) {
      const hit = (STRESS[a] || []).find(([re]) => re.test(e.x));
      if (hit) n += hit[1] * (e.sets || 3);
    }
  }
  return n;
}
// The named lifts behind the load, for the warning ("Back squats and deadlifts").
function stressLifts(t, areas) {
  const out = [];
  for (const d of t.days || []) for (const e of d.exercises || []) {
    for (const a of areas) {
      const hit = (STRESS[a] || []).find(([re, w]) => w === 1 && re.test(e.x));
      if (hit && !out.includes(e.x)) out.push(e.x);
    }
  }
  return out;
}

// ——— Scoring ————————————————————————————————————————————————————————————

const LEVEL_RANK = { new: 0, beginner: 1, intermediate: 2, advanced: 3 };
const GYM_KIT = ['barbell', 'machine', 'cable'];
const EQUIP_PHRASE = {
  barbell: 'a barbell', dumbbell: 'dumbbells', bench: 'a bench', cable: 'cables', machine: 'machines',
  pullup: 'a pull-up bar', kettlebell: 'a kettlebell', bands: 'bands',
};
function joinPhrase(list) {
  if (list.length <= 1) return list[0] || '';
  return list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
}
const levelsOf = (t) => (t.levels && t.levels.length ? t.levels : [t.level || 'beginner']);
function levelFits(t, exp) {
  const lv = levelsOf(t);
  if (lv.includes(exp)) return true;
  // "Brand new" lifters fit any beginner program; "beginner" fits brand-new ones.
  if (exp === 'new') return lv.includes('beginner');
  if (exp === 'beginner') return lv.includes('new');
  return false;
}

export function scoreMatch(t, profile = {}) {
  const p = profile || {};
  const reasons = [];
  const warnings = [];
  let score = 0;
  const { complexity, role } = templateStyle(t);
  const group = GROUP[p.experience] || null;
  const beginner = group === 'N';

  // 1. Equipment — hard requirement.
  const have = profileEquipment(p);
  let equipOk = true;
  if (have) {
    const missing = (t.equipment || []).filter(e => !have.includes(e));
    if (missing.length) {
      equipOk = false;
      score -= 400;
      warnings.push(`Needs ${joinPhrase(missing.map(m => EQUIP_PHRASE[m] || m))}`);
    } else {
      const hasGym = have.filter(e => GYM_KIT.includes(e)).length >= 2;
      const usesGym = (t.equipment || []).some(e => GYM_KIT.includes(e));
      if (hasGym && !usesGym) score -= 60; // home plan for someone with a gym
      else if (hasGym && (t.equipment || []).includes('barbell')) score += 6;
      if (!(t.equipment || []).length && have.length >= 2) score -= 20; // owns kit, gets a no-equipment plan
      // Uses most of what a home lifter owns (kettlebell owner → kettlebell plan).
      const owned = (t.equipment || []).filter(e => have.includes(e)).length;
      if (!hasGym && owned) score += Math.min(12, owned * 4);
      if ((t.equipment || []).length === 0) reasons.push('No equipment needed');
      else if (hasGym) reasons.push(usesGym ? 'Uses your full gym' : `Uses only ${joinPhrase(t.equipment.map(m => EQUIP_PHRASE[m] || m))}`);
      else reasons.push(`Uses only ${joinPhrase(t.equipment.map(m => EQUIP_PHRASE[m] || m))}`);
    }
  }

  // 2. Experience.
  if (p.experience) {
    const expressFit = role === 'express' && p.sessionMinutes && p.sessionMinutes <= 30 && (!p.goal || (t.goals || []).includes(p.goal));
    if (levelFits(t, p.experience) || expressFit) score += 30;
    else {
      const lv = LEVEL_RANK[p.experience] ?? 1;
      const ranks = levelsOf(t).map(l => LEVEL_RANK[l] ?? 1);
      const tooHard = lv < Math.min(...ranks);
      score -= tooHard ? 70 : 25 * (lv - Math.max(...ranks));
      if (tooHard) warnings.push('Built for more experienced lifters');
    }
    if (beginner) {
      if (complexity >= 2) score -= complexity === 3 ? 90 : 60; // periodized: not for a first program
      else reasons.push(templateStyle(t).style === 'linear' ? 'Simple to follow: add a little weight each session' : 'Simple to follow: add reps, then weight');
      if (role === 'volume-block' || role === 'peak' || role === 'specialization' || role === 'high-volume') score -= 80;
    } else {
      if (role === 'volume-block') score -= 70; // a 4–6 week block, not a base program
      if (role === 'peak') score -= p.goal === 'strength' ? 45 : 90; // for a meet date
      if (role === 'specialization') score -= 55;
      if (role === 'high-volume') score -= group === 'A' ? 10 : 40;
      if (group === 'I' && complexity === 1 && t.levels && !levelFits(t, 'intermediate')) score -= 10; // outgrown novice plans
    }
    if (role === 'aging') score -= p.goal === 'general' ? 5 : 40;
    // Brand new: 5–6 training days is more than a first program should ask.
    if (p.experience === 'new' && t.daysPerWeek >= 5) score -= 30;
  }

  // 3a. Days per week — never more than they have.
  if (p.daysPerWeek) {
    const diff = t.daysPerWeek - p.daysPerWeek;
    if (diff === 0) { score += 50; reasons.unshift(`${t.daysPerWeek} days a week, as you asked`); }
    else if (diff > 0) score -= 200 * diff; // they don't have the days
    else {
      const under = -diff;
      // A beginner who offers 5–6 days does best on 3–4: recovery is the limiter.
      const capped = beginner && t.daysPerWeek >= 3 && t.daysPerWeek <= 4 && p.daysPerWeek >= 5;
      score += capped ? 40 - (under - 1) * 4 : under === 1 ? 26 : beginner ? 26 - (under - 1) * 22 : -25 * (under - 1);
      reasons.push(capped ? `${t.daysPerWeek} days a week — plenty while you're building the habit` : `${t.daysPerWeek} days a week, leaving you a spare day`);
    }
  }

  // 3b. Session length.
  if (p.sessionMinutes) {
    const over = t.minutes - p.sessionMinutes;
    if (over <= 0) {
      score += 14;
      reasons.push(`About ${t.minutes} min per session`);
      // Much shorter than they have: fine, a little less ideal.
      if (-over > 40) score -= 10;
    } else if (over <= 5) score += 4;
    else if (over <= 15) score -= (over - 5) * 2;
    else { score -= Math.min(120, over * 3.5); warnings.push(`Sessions run about ${t.minutes} min`); }
    if (role === 'express') score += p.sessionMinutes > 30 ? -30 : !p.goal || (t.goals || []).includes(p.goal) ? 30 : 0;
  }

  // 4a. Goal.
  if (p.goal) {
    if (t.goal === p.goal) score += 24;
    else if ((t.goals || []).includes(p.goal)) score += 12;
    else score -= 90;
  }

  // 4b. The coach's order inside goal × level.
  if (p.goal && group) {
    const list = (PREFER[p.goal] || {})[group] || [];
    const i = list.indexOf(t.id);
    score += i < 0 ? -15 : Math.max(0, 30 - i * 2);
  }

  // 4c. Injuries.
  const areas = injuryAreas(p.injuries);
  if (role === 'joint' && !areas.length) score -= 30; // built for cranky joints
  if (areas.length) {
    const load = stressLoad(t, areas);
    score -= beginner ? Math.min(50, load * 1.5) : Math.min(30, load);
    if (role === 'joint') score += 30 + (p.goal === 'general' || p.goal === 'strength' ? 10 : 0);
    if (load >= 6) {
      const lifts = stressLifts(t, areas).slice(0, 2);
      if (lifts.length) warnings.push(`Has ${joinPhrase(lifts.map(x => x.toLowerCase()))} — swap ${lifts.length > 1 ? 'them' : 'it'} if ${areaPhrase(areas)} complain${complainS(areas)}`);
    } else reasons.splice(1, 0, `Easy on your ${joinPhrase(areas.map(a => AREA_LABEL[a]))}`);
  }

  return { score, reasons: dedupe(reasons).slice(0, 3), warnings, equipOk };
}
const dedupe = (a) => [...new Set(a)];

export function matchPrograms(profile = {}, { limit = TEMPLATES.length, templates = TEMPLATES } = {}) {
  const scored = templates.map((t, i) => ({ template: t, i, ...scoreMatch(t, profile) }));
  scored.sort((a, b) => (b.equipOk - a.equipOk) || (b.score - a.score) || (a.i - b.i));
  // fit: 0–100 against the best match (display only).
  const best = Math.max(1, scored[0] ? scored[0].score : 1);
  return scored.slice(0, limit).map(({ i, ...s }) => ({ ...s, fit: Math.max(0, Math.min(100, Math.round((s.score / best) * 100))), why: explainMatch(s.template, profile) }));
}

// ——— "Why this program", in plain words ————————————————————————————————————

const STYLE_PLAIN = {
  double: 'Each exercise has a rep range. Add a rep or two each time; once every set reaches the top of the range, add a little weight.',
  linear: 'You add a little weight every session while the reps keep coming. If you stall a few times, the weight drops back a bit and builds up again.',
  gzcl: 'Each lift has a heavy, a medium and a light job. The app moves you through them as you progress.',
  rp_sets: 'The number of sets builds up week by week as you get used to the work, then an easy week lets you recover. Weights go up when you hit the top of your reps.',
  rpe_topset: 'One heavy set per main lift, then a few lighter ones. The weights follow how strong you are that day.',
  percent: 'Weights come from a working max for each main lift and move in four-week waves of heavier and lighter weeks.',
};
const GOAL_LINE = {
  hypertrophy: {
    N: 'You want to build muscle and you are starting out, so the plan keeps to a handful of proven lifts repeated often — the fastest way to learn them and grow.',
    I: 'To keep building muscle at your level, each muscle needs plenty of hard sets spread over the week.',
    A: 'At your level, growth comes from more targeted work: enough hard sets per muscle, spread over the week.',
  },
  strength: {
    N: 'You want to get stronger and you are new to it, so this plan sticks to the big barbell lifts and adds weight as often as you can.',
    I: 'Adding weight every session stops working after the first months. This plan uses heavier and lighter weeks so your main lifts keep going up.',
    A: 'Your main lifts move slowly now, so this plan organizes heavy, medium and lighter work in blocks to keep them climbing.',
  },
  powerbuilding: {
    N: 'You want strength and size, so this plan pairs a few heavy main lifts with muscle-building work, and keeps the rules simple.',
    I: 'Heavy days on the main lifts plus higher-rep days for size: the classic way to get stronger and bigger at the same time.',
    A: 'Heavy top sets on the main lifts, then volume for size, organized in short blocks so neither side stalls.',
  },
  general: {
    N: 'You want to feel better and stay consistent, so this plan is short, simple and covers the whole body each session.',
    I: 'A balanced plan that keeps you strong and fit without taking over your week.',
    A: 'A balanced plan that keeps you strong and fit without taking over your week.',
  },
};

export function explainMatch(t, profile = {}) {
  const p = profile || {};
  const group = GROUP[p.experience] || 'I';
  const { style, role } = templateStyle(t);
  const lines = [];
  const goalLine = (GOAL_LINE[p.goal] || GOAL_LINE.general)[group];
  if (role === 'express' && p.sessionMinutes && p.sessionMinutes <= 30) lines.push(`You have about ${p.sessionMinutes} minutes, so this plan pairs exercises back to back to fit a full workout into that time.`);
  else if (role === 'joint' && injuryAreas(p.injuries).length) lines.push('Because you mentioned injuries, this plan uses machines, cables and dumbbells in place of the lifts that load the back and shoulders hardest.');
  else lines.push(goalLine);
  const freq = /full body/i.test(t.split || '') ? t.daysPerWeek : /upper|push|ppl/i.test(t.split || '') && t.daysPerWeek >= 4 ? 2 : 0;
  if (freq >= 2 && (p.goal === 'hypertrophy' || p.goal === 'powerbuilding')) lines[0] += ` This plan trains each muscle ${freq === 2 ? 'twice' : freq === 3 ? 'three times' : `${freq} times`} a week.`;
  const fit = [];
  if (p.daysPerWeek) {
    if (t.daysPerWeek === p.daysPerWeek) fit.push(`fits your ${p.daysPerWeek} days a week`);
    else if (t.daysPerWeek < p.daysPerWeek) fit.push(`uses ${t.daysPerWeek} of your ${p.daysPerWeek} days`);
  }
  if (p.sessionMinutes) fit.push(t.minutes <= p.sessionMinutes ? `takes about ${t.minutes} minutes` : `runs about ${t.minutes} minutes, a bit over your ${p.sessionMinutes}`);
  if (fit.length) lines.push(`It ${joinPhrase(fit)}.${group === 'N' && p.daysPerWeek >= 5 && t.daysPerWeek < p.daysPerWeek ? ' Rest days are when you recover and get stronger, so fewer days is the right start.' : ''}`);
  lines.push(`How you progress: ${STYLE_PLAIN[style] || STYLE_PLAIN.double}`);
  const areas = injuryAreas(p.injuries);
  if (areas.length && role !== 'joint') {
    const lifts = stressLifts(t, areas);
    lines.push(lifts.length
      ? `It includes ${joinPhrase(lifts.slice(0, 2).map(x => x.toLowerCase()))}. If ${areaPhrase(areas)} complain${complainS(areas)}, swap ${lifts.length > 1 ? 'them' : 'it'} from the exercise menu during a workout.`
      : `None of its main lifts load your ${joinPhrase(areas.map(a => AREA_LABEL[a]))} hard.`);
  }
  return { headline: 'Why this program', lines };
}
